/**
 * Date helper utilities for cohort scheduling
 * Centralized to avoid duplication across components
 */

import type { PreferredDay, SessionFrequency } from '../types';
import { DAY_TO_NUMBER, FREQUENCY_DAYS } from '../config/cohortConfig';

/**
 * Ensure a value is a Date object
 * Handles localStorage string restoration and various date formats
 * PRESERVES TIME for datetime values - use parseDateOnly for date-only fields
 */
export function ensureDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    // Handle date-only strings (YYYY-MM-DD) by parsing as local time, not UTC
    // new Date("2026-02-02") parses as UTC midnight, causing timezone shift issues
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    }
    // For full ISO strings, parse normally (preserves time)
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

/**
 * Parse a date string as a DATE ONLY (ignores time component)
 * Use this for date-only fields like due_date, start_date, etc.
 *
 * This function handles the timezone issue where dates stored with time components
 * (e.g., "2026-09-21T07:00:00.000Z") can display as the wrong day when the user's
 * timezone differs from UTC.
 *
 * Examples:
 *   "2026-09-21" -> September 21, 2026 local midnight
 *   "2026-09-21T00:00:00.000Z" -> September 21, 2026 local midnight
 *   "2026-09-21T07:00:00.000Z" -> September 21, 2026 local midnight (NOT September 20!)
 *   "2026-09-21T23:59:59.999Z" -> September 21, 2026 local midnight
 */
export function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;

  // Handle date-only strings (YYYY-MM-DD) - parse as local midnight
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // Handle ANY ISO datetime string (YYYY-MM-DDTHH:MM:SS...) - extract date and create local midnight
  // This prevents timezone shift when displaying dates stored with time components
  if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // Handle ISO strings with timezone offset (e.g., "2026-09-21T00:00:00-07:00")
  if (/^\d{4}-\d{2}-\d{2}[T ]/.test(value)) {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // Fallback: try to parse and extract local date
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/**
 * Whether a DATE-ONLY value falls strictly before today's calendar date.
 *
 * MED-1265: the overdue checks used `new Date(due_date) < new Date()`, which is
 * wrong twice over — the raw parse timezone-shifts UTC-midnight values, and
 * comparing against "now" marks a task due TODAY as overdue the moment the day
 * starts. This compares calendar dates only: due today ⇒ not past.
 */
export function isDateOnlyPast(value: string | Date | null | undefined): boolean {
  const d =
    value instanceof Date
      ? new Date(value.getFullYear(), value.getMonth(), value.getDate())
      : parseDateOnly(value);
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

/**
 * Parse date string as local time (handles YYYY-MM-DD without timezone shift)
 * Alias for ensureDate but throws on invalid input
 */
export function parseLocalDate(value: string): Date {
  const result = ensureDate(value);
  if (!result) {
    throw new Error(`Invalid date string: ${value}`);
  }
  return result;
}

/**
 * Convert Date to ISO date string (YYYY-MM-DD) for API
 * Uses LOCAL date parts to avoid timezone conversion issues
 *
 * Example: If user selects September 21 in their local timezone,
 * this returns "2026-09-21" regardless of UTC offset.
 */
export function toISODateString(date: Date | null | undefined): string | undefined {
  if (!date) return undefined;
  try {
    let dateObj: Date;
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof (date as unknown as { toDate?: () => Date }).toDate === 'function') {
      dateObj = (date as unknown as { toDate: () => Date }).toDate();
    } else {
      dateObj = new Date(date as unknown as string | number);
    }

    if (isNaN(dateObj.getTime())) return undefined;

    // Use local date parts to avoid timezone conversion issues
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return undefined;
  }
}

/**
 * Convert Mantine v8 DateInput/DatePicker onChange value to Date
 *
 * Mantine v8 breaking change: onChange now returns string | null, not Date | null.
 * This helper converts the string value back to a Date object using parseDateOnly
 * to avoid timezone issues.
 *
 * Usage:
 *   <DateInput
 *     value={parseDateOnly(formValue)}
 *     onChange={(value) => setFormValue(toDateValue(value))}
 *   />
 */
export function toDateValue(value: string | null): Date | null {
  return parseDateOnly(value);
}

/**
 * Convert Mantine v8 DateTimePicker onChange value to Date, PRESERVING TIME.
 * Unlike toDateValue (which strips time via parseDateOnly), this function
 * keeps hours/minutes/seconds intact.
 *
 * Mantine v8 emits a space-separated wall-clock string ("YYYY-MM-DD HH:mm:ss").
 * We parse its components explicitly into a LOCAL Date rather than relying on
 * `new Date(string)`: the space-separated form is non-standard, so engines
 * disagree — V8/Chrome parse it as local time, but Safari/JSC return
 * `Invalid Date`. Parsing the parts ourselves makes the picked wall-clock
 * survive identically in every browser, which the ET round-trip depends on
 * (the picked numbers must come back out as the same local components). (MED-1023)
 */
export function toDateTimeValue(value: string | null): Date | null {
  if (!value) return null;

  // Match "YYYY-MM-DD HH:mm[:ss]" or the ISO "YYYY-MM-DDTHH:mm[:ss]" (no offset).
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (match) {
    const [, year, month, day, hours, minutes, seconds] = match;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
      Number(seconds ?? 0),
    );
  }

  // Fallback for any other shape (e.g. offset-bearing or epoch values).
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}


/**
 * Check if a date falls on a preferred day
 */
export function isPreferredDay(
  date: Date | string | null,
  preferredDays: PreferredDay[]
): boolean {
  const checkDate = ensureDate(date);
  if (!checkDate) return true; // Can't check, assume it's fine
  if (!preferredDays || preferredDays.length === 0) return true; // No preference = any day is fine
  const dayOfWeek = checkDate.getDay();
  return preferredDays.some(day => DAY_TO_NUMBER[day] === dayOfWeek);
}

/**
 * Date constraint interface for constraint checking
 */
export interface DateConstraint {
  start_date: string | Date;
  end_date: string | Date;
}

/**
 * Check if a date falls within any constraint range
 */
export function isDateInConstraint(
  date: Date | string | null,
  constraints: DateConstraint[]
): boolean {
  const checkDate = ensureDate(date);
  if (!checkDate) return false;
  if (!constraints || constraints.length === 0) return false;

  const dateTime = checkDate.getTime();

  return constraints.some(constraint => {
    const startDate = ensureDate(constraint.start_date);
    const endDate = ensureDate(constraint.end_date);
    if (!startDate || !endDate) return false;
    const startTime = startDate.getTime();
    const endTime = endDate.getTime();
    return dateTime >= startTime && dateTime <= endTime;
  });
}

/**
 * Get frequency interval in days
 */
export function getFrequencyDays(frequency: SessionFrequency | string | null): number {
  if (!frequency) return FREQUENCY_DAYS.biweekly;
  return FREQUENCY_DAYS[frequency as SessionFrequency] ?? FREQUENCY_DAYS.biweekly;
}

/**
 * Find next valid date that respects preferred days and avoids constraints
 */
export function findNextValidDate(
  startDate: Date,
  intervalDays: number,
  preferredDays: PreferredDay[],
  constraints: DateConstraint[],
  maxIterations: number = 365
): Date {
  const nextDate = new Date(startDate);
  nextDate.setDate(nextDate.getDate() + intervalDays);

  let iterations = 0;
  while (iterations < maxIterations) {
    const inConstraint = isDateInConstraint(nextDate, constraints);
    const onPreferredDay = isPreferredDay(nextDate, preferredDays);

    if (!inConstraint && onPreferredDay) {
      return nextDate;
    }
    nextDate.setDate(nextDate.getDate() + 1);
    iterations++;
  }

  return nextDate;
}

/**
 * Step an anchor date forward by a cadence interval, `steps` times.
 *
 * - weekly:   +7 days per step
 * - biweekly: +14 days per step
 * - monthly:  the SAME calendar day, `steps` months later, clamped to the
 *             target month's length (so Jan 31 + 1 month → Feb 28/29, and
 *             the following step returns to the 31st where the month allows).
 *
 * `steps` is 0-based: step 0 returns the anchor date itself.
 *
 * Unlike findNextValidDate(), this does NOT adjust for preferred days or
 * constraints — the spacing is exactly the selected cadence from the anchor.
 * (MED-1072 AC3 — monthly must be "same day each month", not a fixed 28 days.)
 */
export function addCadenceSteps(
  anchor: Date,
  frequency: SessionFrequency | null,
  steps: number
): Date {
  if (frequency === 'monthly') {
    const year = anchor.getFullYear();
    const month = anchor.getMonth();
    const day = anchor.getDate();
    // Day 0 of the month AFTER the target month === last day of the target month.
    const lastDayOfTargetMonth = new Date(year, month + steps + 1, 0).getDate();
    const clampedDay = Math.min(day, lastDayOfTargetMonth);
    return new Date(year, month + steps, clampedDay);
  }

  const intervalDays = frequency === 'biweekly' ? 14 : 7; // weekly is the default
  const result = new Date(anchor);
  result.setDate(result.getDate() + steps * intervalDays);
  return result;
}

/**
 * Build an evenly-spaced session schedule anchored on `firstDate`.
 *
 * Index 0 is the anchor; each subsequent index follows the cadence exactly
 * (see addCadenceSteps). Returns an array of length `sessionCount`.
 *
 * Differs from generateSessionDates(): this is anchor-relative (every date is
 * computed from the first date, not the previous one) and applies NO
 * constraint/preferred-day adjustment — used for auto-populating a cohort's
 * coaching schedule from the session 1 date. (MED-1072)
 */
export function generateAnchoredSessionDates(
  firstDate: Date,
  sessionCount: number,
  frequency: SessionFrequency | null
): Date[] {
  const dates: Date[] = [];
  for (let i = 0; i < sessionCount; i++) {
    dates.push(addCadenceSteps(firstDate, frequency, i));
  }
  return dates;
}

/**
 * Generate session dates based on first date and frequency
 */
export function generateSessionDates(
  firstDate: Date,
  sessionCount: number,
  frequency: SessionFrequency,
  preferredDays: PreferredDay[],
  constraints: DateConstraint[]
): Date[] {
  const intervalDays = getFrequencyDays(frequency);
  const dates: Date[] = [firstDate];

  for (let i = 1; i < sessionCount; i++) {
    const nextDate = findNextValidDate(
      dates[i - 1],
      intervalDays,
      preferredDays,
      constraints
    );
    dates.push(nextDate);
  }

  return dates;
}
